// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect, useMemo, type ReactElement } from 'react'
import styles from './BrandedLoader.module.css'

const SAMPLE_IMAGES = [
    '/images/1/1.png',
    '/images/2/1.png',
    '/images/3/1.png',
    '/images/4/1.png',
    '/images/1/3.png',
    '/images/2/3.png',
]

const ROTATIONS = [-4, 2, -3, 4, -2, 3]
const OFFSETS = [
    { x: -20, y: -15 },
    { x: 15, y: -10 },
    { x: -15, y: 10 },
    { x: 20, y: -5 },
    { x: -10, y: 15 },
    { x: 10, y: -20 },
]

function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
}

interface BrandedLoaderProps {
    message?: string | null
}

export function BrandedLoader({ message }: BrandedLoaderProps = {}): ReactElement {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [imagesLoaded, setImagesLoaded] = useState(false)
    const shuffledImages = useMemo(() => shuffleArray(SAMPLE_IMAGES), [])

    useEffect(() => {
        const preloadImages = async () => {
            const promises = SAMPLE_IMAGES.map(
                (src) =>
                    new Promise<void>((resolve) => {
                        const img = new Image()
                        img.onload = () => resolve()
                        img.onerror = () => resolve()
                        img.src = src
                    })
            )
            await Promise.all(promises)
            setImagesLoaded(true)
        }
        preloadImages()
    }, [])

    useEffect(() => {
        if (!imagesLoaded) return

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % shuffledImages.length)
        }, 600)

        return () => clearInterval(interval)
    }, [imagesLoaded, shuffledImages.length])

    const visibleIndices = [
        (currentIndex - 2 + shuffledImages.length) % shuffledImages.length,
        (currentIndex - 1 + shuffledImages.length) % shuffledImages.length,
        currentIndex,
    ]

    return (
        <div className={styles.root} role="status" aria-live="polite" aria-label={message ?? 'Loading'}>
            {message && <p className={styles.message}>{message}</p>}
            <div className={styles.imageContainer}>
                {imagesLoaded &&
                    shuffledImages.map((src, index) => {
                        const stackPosition = visibleIndices.indexOf(index)
                        const isVisible = stackPosition !== -1
                        const offset = OFFSETS[index % OFFSETS.length]
                        const rotation = ROTATIONS[index % ROTATIONS.length]

                        return (
                            <div
                                key={index}
                                className={`${styles.image} ${isVisible ? styles.imageVisible : styles.imageHidden}`}
                                style={{
                                    backgroundImage: `url(${src})`,
                                    '--rotation': `${rotation}deg`,
                                    '--offset-x': `${offset.x}px`,
                                    '--offset-y': `${offset.y}px`,
                                    '--z-index': stackPosition,
                                } as React.CSSProperties}
                            />
                        )
                    })}
                <div className={styles.overlay} />
            </div>
            <div className={styles.content}>
                <h1 className={styles.brand}>aether studio</h1>
                <div className={styles.progressBar}>
                    <div className={styles.progressFill} />
                </div>
            </div>
        </div>
    )
}
